import {
  Alert,
  Button,
  Empty,
  Flex,
  Input,
  Segmented,
  Select,
  Table,
  Typography,
} from 'antd';
import { FC, useMemo, useState } from 'react';

import { ReloadOutlined } from '@ant-design/icons';
import { useAdminAccountingUsageController_list } from '@api-client/generated/AdminAccountingUsageController/list';
import AccountingPlanModal from '@entities/accounting-plan';
import { STATUS_COLOR } from '@entities/accounting-plan/format';
import { getApiErrorMessage } from '@utils';

import { UsageRow, getColumns } from './columns';
import * as S from './styled';

const { Text } = Typography;

/** The first year the backend counts usage in (FIRST_USAGE_YEAR). */
const FIRST_YEAR = 2024;

type Filter = 'all' | 'over' | 'near' | 'documents' | 'legacy';

const FILTERS: { label: string; value: Filter }[] = [
  { label: 'All', value: 'all' },
  { label: 'Over plan', value: 'over' },
  { label: 'Near limit', value: 'near' },
  { label: 'Bookings', value: 'documents' },
  { label: 'Legacy turnover', value: 'legacy' },
];

const matches: Record<Filter, (row: UsageRow) => boolean> = {
  all: () => true,
  over: (row) => row.status === 'over_plan',
  near: (row) => row.status === 'near_limit',
  documents: (row) => row.planType === 'documents',
  legacy: (row) => row.planType === 'legacy_turnover',
};

/**
 * "Accounting usage": the yearly accounting plan use of every client with a plan, and the "Edit plan"
 * popup. Admin only (CSMs work under it), like the API behind it. The numbers come from the usage
 * sync, which runs every three days, so there is no recalculate button.
 */
const AccountingUsage: FC = () => {
  const currentYear = new Date().getUTCFullYear();
  const [year, setYear] = useState(currentYear);
  const [filter, setFilter] = useState<Filter>('all');
  const [search, setSearch] = useState('');
  const [editing, setEditing] = useState<UsageRow | null>(null);

  const { data, isLoading, error, refetch } =
    useAdminAccountingUsageController_list({ params: { year } });
  const all = useMemo(() => data?.rows ?? [], [data]);
  // Nothing to count yet — loading, or a load that failed: '—' rather than a confident 0.
  const unknown = isLoading || (Boolean(error) && !data);

  const rows = useMemo(() => {
    const term = search.trim().toLowerCase();
    return all.filter(
      (row) =>
        matches[filter](row) &&
        (!term || row.companyName.toLowerCase().includes(term))
    );
  }, [all, filter, search]);

  // Over every company of the year, not the filtered subset (spec §3.1).
  const stats = useMemo(
    () => ({
      over: all.filter(matches.over).length,
      near: all.filter(matches.near).length,
      legacy: all.filter(matches.legacy).length,
    }),
    [all]
  );

  // Plans are edited as they are now — the API has no per-year plan — so only from the current year,
  // whose rows show that plan; a past year shows the plan in force when it ended.
  const editable = year === currentYear;
  const columns = useMemo(() => getColumns(setEditing, editable), [editable]);

  const years = Array.from(
    { length: currentYear - FIRST_YEAR + 1 },
    (_, i) => currentYear - i
  );

  return (
    <Flex gap={16} vertical>
      <S.Heading>Accounting usage</S.Heading>

      <Flex align="center" gap={12} wrap="wrap">
        <Select
          aria-label="Year"
          value={year}
          onChange={setYear}
          options={years.map((value) => ({ value, label: String(value) }))}
          style={{ width: 96 }}
        />
        <Segmented<Filter>
          options={FILTERS}
          value={filter}
          onChange={setFilter}
        />
        <Input
          allowClear
          aria-label="Company name"
          placeholder="Company name"
          style={{ width: 180 }}
          value={search}
          onChange={(event) => setSearch(event.target.value)}
        />
      </Flex>

      <Flex gap={8} wrap="wrap">
        <S.Stat>
          <Text>Clients</Text>
          <S.StatValue>{unknown ? '—' : all.length}</S.StatValue>
        </S.Stat>
        <S.Stat>
          <Text>Over plan</Text>
          <S.StatValue color={STATUS_COLOR.over_plan}>
            {unknown ? '—' : stats.over}
          </S.StatValue>
        </S.Stat>
        <S.Stat>
          <Text>Near limit (≥80%)</Text>
          <S.StatValue color={STATUS_COLOR.near_limit}>
            {unknown ? '—' : stats.near}
          </S.StatValue>
        </S.Stat>
        <S.Stat>
          <Text>Legacy plans</Text>
          <span>
            <S.StatValue>{unknown ? '—' : stats.legacy}</S.StatValue>{' '}
            <Text type="secondary">of {unknown ? '—' : all.length}</Text>
          </span>
        </S.Stat>
      </Flex>

      {/* A failed load replaces only the table: the year and filters stay, so the admin can leave a
          failing year. A failed background refetch keeps the table it already has. */}
      {error && !data ? (
        <Alert
          showIcon
          type="error"
          message="Could not load accounting usage"
          description={getApiErrorMessage(error, 'Try again in a moment.')}
          action={
            <Button
              size="small"
              icon={<ReloadOutlined />}
              onClick={() => refetch()}
            >
              Try again
            </Button>
          }
        />
      ) : (
        <Table<UsageRow>
          size="small"
          columns={columns}
          dataSource={rows}
          loading={isLoading}
          rowKey={(row) => row.companyId}
          scroll={{ x: 1150 }}
          pagination={{
            defaultPageSize: 50,
            showSizeChanger: true,
            hideOnSinglePage: true,
          }}
          locale={{
            emptyText: <Empty description="No clients match this filter." />,
          }}
        />
      )}

      {editing && (
        <AccountingPlanModal
          companyId={editing.companyId}
          companyName={editing.companyName}
          usage={{
            bookableDocuments: editing.bookableDocuments,
            turnover: editing.turnover,
          }}
          onClose={() => setEditing(null)}
        />
      )}
    </Flex>
  );
};

export default AccountingUsage;
